import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import supplierService from '../../services/supplierService';
import factureService from '../../services/factureService';
import paymentService from '../../services/paymentService';
import authService from '../../services/authService';
import FournisseursLabosSwitch from '../../components/FournisseursLabosSwitch';
import SupplierFormModal from '../../components/SupplierFormModal';
import SupplierDetail from './SupplierDetail';
import { IcPlus, IcSearch, IcBuilding, IcTruck, IcReceiptText, IcBanknote, IcChevronLeft, IcChevronRight, IcClose, IcAlertCircle } from '../../theme/icons';
import { TableSkeleton } from '../../components/Skeleton';
import { filterSearch } from '../../utils/search';

// Pagination (owner 2026-09-09) : même page de 10 que « Liste des produits ».
const PAGE_SIZE = 10;

const SupplierList = () => {
  // Sellers can browse suppliers and open a supplier's detail, but not see the
  // amounts owed or record acomptes (financial). Manager/admin: full.
  const canManage = authService.isManagerOrAdmin();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  // Supplier autocomplete + acompte (mirrors the facture list)
  const [pickedSupplier, setPickedSupplier] = useState(null);
  const [showSupplierSuggest, setShowSupplierSuggest] = useState(false);
  const [activeSuggest, setActiveSuggest] = useState(-1); // keyboard-highlighted suggestion
  const [supplierFactures, setSupplierFactures] = useState([]);
  const [acompte, setAcompte] = useState('');
  const [applyingAcompte, setApplyingAcompte] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompteError, setAcompteError] = useState(null);
  const activeItemRef = useRef(null);
  // Panneau « Nouveau fournisseur » (même modale que « Nouveau laboratoire »)
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  // Panneau fiche fournisseur : un clic sur une ligne ouvre la fiche ici,
  // sans quitter la liste. La liste se rafraîchit à la fermeture (acompte,
  // modification ou suppression faits dans le panneau).
  // La fiche ouverte vit dans l'URL : /suppliers?fiche=<id>. Ainsi la page
  // facture (« Retour ») et le bouton Précédent du navigateur ramènent sur la
  // liste AVEC le même panneau ouvert.
  const [searchParams, setSearchParams] = useSearchParams();
  const ficheId = searchParams.get('fiche');
  const openSupplier = useMemo(() => {
    if (!ficheId) return null;
    return suppliers.find((s) => String(s.id) === ficheId) || { id: ficheId, name: '' };
  }, [ficheId, suppliers]);
  // (react-router 6.3 : pas de forme fonctionnelle pour setSearchParams.)
  const openSupplierPanel = (supplier) => {
    const next = new URLSearchParams(searchParams);
    next.set('fiche', String(supplier.id));
    setSearchParams(next);
  };
  const closeSupplierPanel = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('fiche');
    setSearchParams(next);
    fetchSuppliers();
  };

  useEffect(() => {
    if (!openSupplier) return undefined;
    // Une couche au-dessus (panneau facture, modale acompte/modifier/reçu) ouverte : Échap lui revient.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelectorAll('.modal-backdrop').length > 1) return;
      closeSupplierPanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSupplier]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService.getAllSuppliers();

      // Handle both paginated and non-paginated responses
      const suppliersData = Array.isArray(data) ? data : (data.results || []);

      setSuppliers(suppliersData);
      setFilteredSuppliers(suppliersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Handle search input change
  useEffect(() => {
    // Règle Ki Para (utils/search.js) : début de mot sur le nom, « contient »
    // sur téléphone / e-mail, « commence par » en tête.
    setFilteredSuppliers(filterSearch(suppliers, searchTerm, {
      text: (s) => s.name, codes: (s) => [s.phone, s.email],
    }));
  }, [searchTerm, suppliers]);

  // Pagination côté client — mêmes calculs et même pied que ProductList /
  // LaboratoireList (owner 2026-09-09 : même gabarit de page que « Liste des
  // produits », 10 lignes, donc même hauteur de page et même défilement).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [searchTerm]);
  const totalCount = filteredSuppliers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);
  const displayedSuppliers = useMemo(
    () => filteredSuppliers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredSuppliers, page]
  );
  const pageButtons = useMemo(() => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  const formatDue = (value) => `${(Number(value) || 0).toFixed(3)} DT`;

  // Supplier suggestions for the search box (only when typing & nothing picked)
  const supplierSuggestions = (!pickedSupplier && searchTerm.trim())
    ? filterSearch(suppliers, searchTerm, { text: (s) => s.name, codes: (s) => [s.phone] }).slice(0, 8)
    : [];

  // Outstanding total of the picked supplier (sum of its factures' amount_due)
  const supplierUnpaidTotal = supplierFactures.reduce((s, f) => s + (Number(f.amount_due) || 0), 0);

  // Pick a supplier -> load its factures so we can apply an acompte
  const handlePickSupplier = async (s) => {
    setPickedSupplier(s);
    setSearchTerm(s.name);
    setShowSupplierSuggest(false);
    setActiveSuggest(-1);
    setAcompte('');
    try {
      const data = await factureService.getAllFactures({ supplier_id: s.id });
      setSupplierFactures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching supplier factures:', err);
      setSupplierFactures([]);
    }
  };

  const clearPicked = () => {
    setSearchTerm('');
    setPickedSupplier(null);
    setSupplierFactures([]);
    setAcompte('');
    setShowSupplierSuggest(false);
    setActiveSuggest(-1);
  };

  // Keyboard navigation for the supplier autocomplete (down/up/Enter/Escape)
  const handleSearchKeyDown = (e) => {
    if (!showSupplierSuggest || supplierSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggest((i) => Math.min(i + 1, supplierSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggest((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeSuggest >= 0 && activeSuggest < supplierSuggestions.length) {
        e.preventDefault();
        handlePickSupplier(supplierSuggestions[activeSuggest]);
      }
    } else if (e.key === 'Escape') {
      setShowSupplierSuggest(false);
      setActiveSuggest(-1);
    }
  };

  // Keep the keyboard-highlighted suggestion scrolled into view
  useEffect(() => {
    if (activeItemRef.current) activeItemRef.current.scrollIntoView({ block: 'nearest' });
  }, [activeSuggest]);

  // ----- Acompte (same modal design & flow as the supplier detail page) -----
  const handleOpenAcompteModal = () => {
    setAcompte('');
    setAcompteError(null);
    setShowAcompteModal(true);
  };

  const handleApplyAcompte = async (e) => {
    e.preventDefault();
    const amount = parseFloat(acompte);
    if (!amount || isNaN(amount) || amount <= 0) {
      setAcompteError('Veuillez entrer un montant valide.');
      return;
    }
    if (amount > supplierUnpaidTotal + 1e-6) {
      setAcompteError('Le montant dépasse le reste à payer du fournisseur.');
      return;
    }
    const unpaid = supplierFactures
      .filter((f) => (Number(f.amount_due) || 0) > 0)
      .sort((a, b) => new Date(a.date || a.date_added || 0) - new Date(b.date || b.date_added || 0));

    setApplyingAcompte(true);
    setAcompteError(null);
    const EPS = 1e-6;
    // If the user is paying (essentially) the whole balance, clear it exactly so no
    // sub-millime float dust is left behind on the supplier.
    const payAll = amount >= supplierUnpaidTotal - 5e-4;
    let remaining = payAll ? supplierUnpaidTotal : amount;
    let totalPaid = 0;
    try {
      for (const f of unpaid) {
        if (remaining <= EPS) break;
        const due = Number(f.amount_due) || 0;
        if (due <= 0) continue;
        // Pay the facture in full when the remaining covers it — send its EXACT
        // amount_due so we never round above it (the backend rejects amount > due,
        // and stored dues can carry >3 decimals from float arithmetic).
        const pay = remaining >= due - EPS ? due : remaining;
        if (pay <= 0) continue;
        await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
        remaining -= pay;
        totalPaid += pay;
      }
      // Record the acompte as one dated payment in the supplier's payment history.
      if (totalPaid > EPS) {
        try {
          await paymentService.create({
            payment_type: 'supplier',
            supplier: pickedSupplier.id,
            source: 'acompte',
            amount: totalPaid,
            note: 'Acompte sur factures',
          });
        } catch (payErr) {
          // Don't fail the acompte if only the history record couldn't be written.
          console.error('Acompte recorded on factures but payment log failed:', payErr);
        }
      }
      setShowAcompteModal(false);
      setAcompte('');
      // Refresh both the table amounts and the picked supplier's factures
      await fetchSuppliers();
      const data = await factureService.getAllFactures({ supplier_id: pickedSupplier.id });
      setSupplierFactures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Acompte error:', err);
      setAcompteError("Erreur lors de l'enregistrement de l'acompte.");
    } finally {
      setApplyingAcompte(false);
    }
  };

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Owner 2026-09-09 : ligne d'en-tête = la bande d'onglets de « Nouvelle
          vente » (VenteTabs) — chips Fournisseurs | Laboratoires .tab-chip /
          .tab-chip-on (42px, gap-2), mb-4 sous la ligne ; mini « + »
          (btn-mini, « Ajouter un fournisseur ») à droite (ml-auto, même emplacement que la bascule
          Produits/Services là-bas). Ouvre un panneau, plus de page à part. */}
      <div className="mb-4">
        <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {/* h-[46px] = la rangée de titre de « Liste des produits » (la pastille
              Promo y fait 46 px) : le chip et la barre tombent au même pixel. */}
          <div className="flex gap-2 px-1 min-w-full items-center h-[46px]">
            <FournisseursLabosSwitch variant="chips" />
            {canManage && (
              <div className="ml-auto inline-flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowSupplierModal(true)}
                  className="h-9 w-9 flex items-center justify-center btn-mini transition-colors"
                  title="Ajouter un fournisseur"
                  aria-label="Ajouter un fournisseur"
                >
                  <IcPlus />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barre de recherche = celle de « Nouvelle vente » / « Liste des
          produits » (owner 2026-09-09) : flex gap-3 mb-3, champ .inp h-12
          pl-11 pr-10, loupe #6B7280 à 14px du bord, ✕ à droite. Garde
          l'autocomplétion fournisseur (acompte) au kit .ac-list collé sous
          le champ. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1 min-w-0">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
          <input
            type="text"
            className="inp h-12 w-full pl-11 pr-10"
            placeholder="Rechercher un fournisseur (nom, email ou téléphone)..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPickedSupplier(null); setSupplierFactures([]); setShowSupplierSuggest(true); setActiveSuggest(-1); }}
            onFocus={() => setShowSupplierSuggest(true)}
            onBlur={() => setTimeout(() => setShowSupplierSuggest(false), 150)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
          />
          {(searchTerm || pickedSupplier) && (
            <button
              type="button"
              onClick={clearPicked}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Effacer"
            >
              <IcClose />
            </button>
          )}
          {showSupplierSuggest && !pickedSupplier && supplierSuggestions.length > 0 && (
            <div className="ac-list max-h-72 overflow-auto">
              {supplierSuggestions.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  ref={i === activeSuggest ? activeItemRef : null}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePickSupplier(s)}
                  onMouseEnter={() => setActiveSuggest(i)}
                  className={`ac-row px-4 py-2.5 w-full text-left ${i === activeSuggest ? 'ac-row-on' : ''}`}
                >
                  <span className="ac-name text-sm">{s.name}</span>
                  {s.phone && <span className="ac-sub text-xs">{s.phone}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Acompte card — same design & logic as the supplier detail page (manager/admin) */}
      {canManage && pickedSupplier && (
        <div className="mb-6 max-w-sm kpi kpi-coral cursor-default">
          <IcAlertCircle size={16} className="kpi-icon" />
          <p className="kpi-label uppercase truncate pr-6">Reste à payer · {pickedSupplier.name}</p>
          <p className="kpi-value">{supplierUnpaidTotal.toFixed(3)} DT</p>
          {supplierUnpaidTotal > 0.0005 ? (
            <button
              onClick={handleOpenAcompteModal}
              className="mt-3 w-full flex items-center justify-center gap-2 text-sm py-2 btn-p transition-colors"
            >
              <IcBanknote /> Enregistrer un acompte
            </button>
          ) : (
            <p className="text-xs text-green-600 mt-2 font-medium">Tout est payé pour ce fournisseur</p>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={10} columns={[
              { label: 'Fournisseur', avatar: true, w: 300 },
              { label: 'Factures', w: 80 },
              { label: 'Payement', align: 'right', w: 72 },
              { label: 'Contact', w: 80 },
            ]} />
      ) : filteredSuppliers.length === 0 ? (
        /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase 13px 600 */
        <div className="empty-ki rounded-2xl p-12">
          <IcBuilding size={36} className="empty-ki-icon w-9 h-9 mb-3" />
          <p className="empty-ki-title text-base mt-0">
            {searchTerm ? 'Aucun fournisseur correspondant à la recherche' : 'Aucun fournisseur trouvé'}
          </p>
        </div>
      ) : (
        /* Liste = celle de « Liste des produits » (owner 2026-09-09) : carte
           blanche rayon 16 bordée gray-200, tableau dense Composants-2 §6 —
           en-tête th-ki px-5 py-3 text-xs, lignes px-5 py-4 text-sm, nom 600,
           montant tabulaire 600. Contenu des lignes = l'ancien (owner
           2026-09-09, « back content ») : pastille camion grise bordée,
           chevron › dans la cellule Contact.
           Hauteur (owner 2026-09-09, dernier mot) : comme « Liste des
           produits » — 10 lignes de 81 px + pied de pagination, la carte prend
           la hauteur de ses lignes, la page défile exactement comme /products. */
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th scope="col" className="th-ki px-5 py-3 text-xs text-left uppercase">Fournisseur</th>
                  <th scope="col" className="th-ki px-5 py-3 text-xs text-left uppercase">Factures</th>
                  {canManage && (
                    <th scope="col" className="th-ki px-5 py-3 text-xs text-right uppercase">Payement</th>
                  )}
                  <th scope="col" className="th-ki px-5 py-3 text-xs text-left uppercase">Contact</th>
                </tr>
              </thead>
              <tbody>
                {displayedSuppliers.map((supplier) => {
                  const amountDue = Number(supplier.total_amount_due) || 0;
                  const factureCount = supplier.facture_count || 0;
                  return (
                    <tr
                      key={supplier.id}
                      onClick={() => openSupplierPanel(supplier)}
                      className="border-t border-gray-100 tr-ki transition-colors"
                    >
                      <td className="td-ki px-5 py-4 text-sm">
                        <div className="flex items-center gap-3">
                          {/* 48 px = l'image produit de « Liste des produits » → lignes de 81 px */}
                          <span className="h-12 w-12 rounded-xl bg-gray-100 text-gray-700 border border-gray-500 flex items-center justify-center text-sm shrink-0">
                            <IcTruck />
                          </span>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">{supplier.name}</div>
                            <div className="text-xs text-gray-400">{supplier.address || 'Adresse non renseignée'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-ki px-5 py-4 text-sm">
                        <span className="inline-flex items-center text-gray-700">
                          <IcReceiptText className="mr-2 text-gray-400" />
                          {factureCount} facture{factureCount > 1 ? 's' : ''}
                        </span>
                      </td>
                      {canManage && (
                        <td className="td-ki px-5 py-4 text-sm text-right tabular-nums whitespace-nowrap">
                          <span className={`inline-flex items-center font-semibold ${amountDue > 0 ? 'text-red-600' : 'text-green-700'}`}>
                            <IcBanknote className="mr-2 text-gray-400" />
                            {formatDue(amountDue)}
                          </span>
                          <div className="text-xs text-gray-400 mt-0.5">{amountDue > 0 ? 'Reste à payer' : 'Soldé'}</div>
                        </td>
                      )}
                      <td className="td-ki px-5 py-4 text-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-gray-700">{supplier.email || '-'}</div>
                            <div className="text-gray-400 tabular-nums">{supplier.phone || '-'}</div>
                          </div>
                          <IcChevronRight className="text-gray-400 ml-4 flex-shrink-0" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pied de pagination = celui de « Liste des produits » (Composants-2
              §4) : compte total à gauche 12.5px gris tabulaire, pages en carrés
              34px, dernière page largeur auto. */}
          <div className="flex flex-col sm:flex-row items-center px-5 py-4 border-t border-gray-100 bg-white gap-3">
            <div className="text-[12.5px] text-gray-500 tabular-nums flex-1 w-full sm:w-auto">
              {totalCount === 0 ? '0 fournisseur' : `${rangeStart}–${rangeEnd} sur ${totalCount} fournisseur${totalCount > 1 ? 's' : ''}`}
            </div>
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label="Page précédente"
              >
                <IcChevronLeft className="w-3 h-3" />
              </button>
              {pageButtons.map((p, idx) => {
                const prev = pageButtons[idx - 1];
                const gap = prev && p - prev > 1;
                const wide = p === totalPages && p > 1;
                return (
                  <React.Fragment key={p}>
                    {gap && <span className="pg-dots w-auto h-auto px-2">…</span>}
                    <button
                      type="button"
                      className={`${p === page ? 'pg-on' : 'pg-btn'}${wide ? ' w-auto px-2' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button
                type="button"
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-label="Page suivante"
              >
                <IcChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panneau fiche fournisseur — même page SupplierDetail, rendue dans un
          panneau z-50 (la barre de navigation est sticky z-50 : même couche,
          le panneau vient après dans le DOM donc passe devant) ; ses propres
          modales (acompte, modifier, reçu, facture) viennent encore après. */}
      {openSupplier && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={closeSupplierPanel}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Fournisseur ${openSupplier.name}`}
          >
            {/* La fiche rend elle-même son entête encre (nom, contact, ✕),
                ses cases, l'historique à onglets et son pied — même squelette
                que la fiche client. */}
            <SupplierDetail key={openSupplier.id} supplierId={openSupplier.id} onClose={closeSupplierPanel} />
          </div>
        </div>
      )}

      {/* Panneau « Nouveau fournisseur » — la liste se rafraîchit à la création */}
      <SupplierFormModal
        open={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onCreated={fetchSuppliers}
      />

      {/* Acompte Modal — same design & flow as the supplier detail page */}
      {showAcompteModal && pickedSupplier && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setShowAcompteModal(false)}>
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-2xl">
              <h2 className="modal-title text-lg">Enregistrer un acompte</h2>
              <button onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{pickedSupplier.name}</p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Reste à payer (total)</span>
                <span className="font-semibold text-red-600">{supplierUnpaidTotal.toFixed(3)} DT</span>
              </div>
              <p className="text-xs text-gray-400 pt-1">
                L'acompte est appliqué aux factures impayées, des plus anciennes aux plus récentes.
              </p>
            </div>

            <form onSubmit={handleApplyAcompte}>
              {acompteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-4">{acompteError}</div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5">Montant de l'acompte</label>
                <div className="relative">
                  <input
                    type="number"
                    value={acompte}
                    onChange={(e) => setAcompte(e.target.value)}
                    min="0"
                    max={supplierUnpaidTotal}
                    step="0.001"
                    className="inp w-full px-4 py-3 rounded-xl text-base text-gray-900"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              {/* Composants-2 §10 : pied à droite — Annuler ghost + Confirmer primaire vert (.btn-p, owner 2026-09-10) */}
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAcompteModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
                <button
                  type="submit"
                  disabled={applyingAcompte}
                  className="px-6 py-2.5 btn-p transition-colors"
                >
                  {applyingAcompte ? 'Enregistrement...' : "Confirmer l'acompte"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierList;
